import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  ExternalLink,
  Files,
  LayoutGrid,
  Rocket,
  Settings,
  Sparkles,
  Waypoints,
} from 'lucide-react';

const deploymentTargets = [
  {
    name: 'MapleOS',
    badge: 'Recommended',
    description:
      'The best home for a finished model. MapleOS turns your trained system into something people can actually use across real product surfaces, not just a demo tab.',
    points: [
      'Bring your model into MapleOS and make it available across our AI Surfaces.',
      'Give your team one place for files, context, workflows, and execution.',
      'Use the sister product advantage: one training partner, one clean deployment path.',
    ],
    href: 'https://mapleos.ai/docs',
    cta: 'Read MapleOS docs',
  },
  {
    name: 'Claude and companion workflows',
    badge: 'Popular',
    description:
      'For teams already living in Claude-style workflows, we can shape delivery around ecosystems that support custom models and bring your trained system into the tools people already know.',
    points: [
      'Good fit for teams that want to stay close to familiar chat-led workflows.',
      'Useful when adoption depends on meeting users where they already work.',
      'Ideal for pilots, internal rollouts, and model-specific workflows.',
    ],
    href: '/purchase',
    cta: 'Plan your rollout',
  },
  {
    name: 'Open-source stacks',
    badge: 'Flexible',
    description:
      'If your team prefers open infrastructure, we can prepare your trained model for open-source ecosystems that support custom model usage and self-directed deployment.',
    points: [
      'Fits teams that want more control over hosting, tooling, or runtime choices.',
      'Works well for technical groups building around open model infrastructure.',
      'Keeps your deployment options wide open as your needs evolve.',
    ],
    href: '/purchase',
    cta: 'Explore deployment options',
  },
];

const mapleSurfaces = [
  {
    title: 'AI Center',
    description: 'Give people a clear starting point instead of dropping them into a blank prompt.',
    icon: Sparkles,
  },
  {
    title: 'Knowledge Base',
    description: 'Keep your model grounded in source material, notes, and reusable context.',
    icon: LayoutGrid,
  },
  {
    title: 'Files',
    description: 'Connect the model to real working material so outputs stay tied to the job at hand.',
    icon: Files,
  },
  {
    title: 'AI Blueprints',
    description: 'Turn repeatable work into dependable workflows your team can relaunch with confidence.',
    icon: Waypoints,
  },
  {
    title: 'Settings',
    description: 'Keep control visible so teams can shape how the system behaves in practice.',
    icon: Settings,
  },
];

const rolloutSteps = [
  {
    title: 'You fund the build',
    description:
      'You work with us on the training scope, the data story, and the outcome that matters to your team.',
  },
  {
    title: 'We deliver the trained model',
    description:
      'Once the model is ready, we prepare it for the deployment path that matches how your people actually work.',
  },
  {
    title: 'You launch it where it counts',
    description:
      'MapleOS is the headline path, but we also support delivery into other ecosystems that allow custom model use.',
  },
];

const highlights = [
  {
    title: 'SLM and on-device first',
    description: 'CanXP AI specializes in small language models and on-device systems built for speed, privacy, and practical deployment.',
  },
  {
    title: 'MapleOS comes first',
    description: 'Because it is our sister product, it gives you the fastest path from finished model to full product experience.',
  },
  {
    title: 'Built for real adoption',
    description: 'The goal is not to hand you a model file and disappear. The goal is to help your team actually use it.',
  },
  {
    title: 'Flexible delivery',
    description: 'If MapleOS is not the only destination, we can shape rollout plans around the ecosystems your team already depends on.',
  },
];

const AgentMarketplace = () => {
  return (
    <MainLayout>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-6 md:px-6 lg:py-8">
        <section className="overflow-hidden rounded-3xl border bg-gradient-to-br from-card via-card to-canada-red/5">
          <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.4fr_0.9fr] lg:px-10 lg:py-10">
            <div className="space-y-5">
              <Badge className="w-fit bg-canada-red text-white hover:bg-canada-red/90">
                Deployment Hub
              </Badge>
              <div className="space-y-3">
                <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
                  Small models. Real products. Built to ship.
                </h1>
                <p className="max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
                  CanXP AI specializes in SLMs and on-device models for teams that want speed,
                  privacy, and something they can actually deploy. You hire us to build the model.
                  When it is ready, we help you put it to work — with MapleOS as the cleanest path
                  to a full product experience across real AI Surfaces.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg" className="bg-canada-red text-white hover:bg-canada-red/90">
                  <a href="https://mapleos.ai/docs" target="_blank" rel="noreferrer">
                    Open MapleOS docs
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="/purchase">
                    Start a training plan
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {highlights.map((item) => (
                <Card key={item.title} className="border-border/70 bg-background/70 backdrop-blur">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          {rolloutSteps.map((step, index) => (
            <Card key={step.title}>
              <CardHeader>
                <Badge variant="outline" className="w-fit">
                  Step {index + 1}
                </Badge>
                <CardTitle className="pt-3 text-xl">{step.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">{step.description}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">Choose the ecosystem that fits the job</h2>
            <p className="max-w-3xl text-muted-foreground">
              We build compact, practical models meant to be used in the real world. MapleOS is the lead story here for a reason:
              it gives your trained model a full environment, not just a single entry point. But if your rollout needs a different path,
              we can support that too.
            </p>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            {deploymentTargets.map((target) => (
              <Card key={target.name} className="flex h-full flex-col justify-between">
                <CardHeader className="space-y-3">
                  <Badge variant={target.name === 'MapleOS' ? 'default' : 'secondary'} className={target.name === 'MapleOS' ? 'w-fit bg-canada-red text-white hover:bg-canada-red/90' : 'w-fit'}>
                    {target.badge}
                  </Badge>
                  <div>
                    <CardTitle className="text-2xl">{target.name}</CardTitle>
                    <CardDescription className="pt-2 text-sm leading-6">
                      {target.description}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {target.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm leading-6 text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-canada-red" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full" variant={target.name === 'MapleOS' ? 'default' : 'outline'}>
                    <a
                      href={target.href}
                      {...(target.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
                    >
                      {target.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
          <Card className="border-canada-red/20 bg-canada-red/5">
            <CardHeader>
              <Badge variant="outline" className="w-fit border-canada-red/30 text-canada-red">
                Why MapleOS gets the spotlight
              </Badge>
              <CardTitle className="text-2xl">It turns your trained model into a product experience.</CardTitle>
              <CardDescription className="text-sm leading-6">
                MapleOS is not just a place to park a model. It is where a compact, deployable system becomes useful across a connected environment.
                That is why we push it hard here: it is the strongest path from custom training to everyday value.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm leading-6 text-muted-foreground">
                Our sweet spot is SLMs and on-device models that need to feel fast, practical, and ready for daily use.
                Instead of asking your team to live inside one narrow interface, MapleOS gives your model room to work across
                orientation, files, knowledge, workflows, and user-controlled settings. That makes adoption easier and the value easier to see.
              </p>
              <Button asChild className="bg-canada-red text-white hover:bg-canada-red/90">
                <a href="https://mapleos.ai/docs" target="_blank" rel="noreferrer">
                  See the MapleOS app ecosystem
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {mapleSurfaces.map((surface) => {
              const Icon = surface.icon;
              return (
                <Card key={surface.title}>
                  <CardHeader>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-canada-red/10 text-canada-red">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="pt-3 text-lg">{surface.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-6 text-muted-foreground">{surface.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <section>
          <Card className="border-dashed">
            <CardContent className="flex flex-col gap-4 px-6 py-8 md:flex-row md:items-center md:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-canada-red">
                  <Rocket className="h-5 w-5" />
                  <span className="text-sm font-medium">Ready for the handoff?</span>
                </div>
                <h2 className="text-2xl font-semibold tracking-tight">Train with us, then launch where it matters.</h2>
                <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                  We build focused SLMs and on-device models with you. Then we help you put them in front of users through MapleOS
                  or another compatible ecosystem. That is the whole point: not just training, but deployment that leads to real use.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild className="bg-canada-red text-white hover:bg-canada-red/90">
                  <a href="/purchase">
                    View training plans
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a href="https://mapleos.ai/docs" target="_blank" rel="noreferrer">
                    Explore MapleOS
                    <Boxes className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </MainLayout>
  );
};

export default AgentMarketplace;